/**
 * Turn state machine for the simplified teaching variant. Pure data — no
 * canvas, no DOM, no timers. Every transition validates its preconditions and
 * returns a NEW `MahjongState`; unchanged per-seat arrays may be shared by
 * reference (standard immutable update). Illegal transitions throw — the UI
 * and AI only ever offer legal actions.
 *
 * This module is the public API. The state shape + shared helpers live in
 * `stateCore.ts`; the claim-window transitions live in `claimTransitions.ts`;
 * both are re-exported here so callers import everything from `GameState`.
 * See `stateCore.ts` for the tile-conservation invariant.
 */

import { SEAT_COUNT, type Meld, type Seat, type Tile } from './types.js';
import { buildWall, dealHands, splitDeadWall } from './Wall.js';
import { isWinningHand } from './WinChecker.js';
import { availableClaims, canRon, type SeatClaims } from './Claims.js';
import {
  DEALER,
  emptyPerSeat,
  requirePhase,
  type MahjongState,
} from './stateCore.js';
import { SimplifiedRuleset } from './ruleset/SimplifiedRuleset.js';
import type { Ruleset } from './ruleset/types.js';

export type { Phase, HandResult, MahjongState } from './stateCore.js';
export type { ChiOption, Claim, SeatClaims } from './Claims.js';
export { declareRon, claimPung, claimChi, passClaims } from './claimTransitions.js';
export { declareConcealedKong, claimExposedKong, declareAddedKong } from './kongTransitions.js';

/** Seed recorded on a crafted (deal-supplied) hand — provenance only, never rolled. */
const CRAFTED_DEAL_SEED = 0;

/**
 * Builds an opening hand from an explicit deal — crafted per-seat hands plus the
 * wall — instead of a seed. The dealer draws the opening (14th) tile off the
 * front of `wall`, exactly like `createHand`, so lessons and tests can pin every
 * tile without seed-fishing. `seed` is stored for provenance only.
 */
export function createHandFromDeal(
  hands: readonly (readonly Tile[])[],
  wall: readonly Tile[],
  seed: number = CRAFTED_DEAL_SEED,
  ruleset: Ruleset = SimplifiedRuleset,
  dealer: Seat = DEALER,
): MahjongState {
  const nextHands = hands.map((h) => [...h]);
  // Kong replacement draws come from a reserved dead wall, never the live
  // wall — but reserving it unconditionally would shrink Simplified's live
  // wall and shift when hands exhaust, so it only applies when kong is on.
  const { liveWall, deadWall } = ruleset.kongEnabled
    ? splitDeadWall(wall)
    : { liveWall: wall, deadWall: [] as readonly Tile[] };
  const remaining = [...liveWall];
  const drawn = remaining.shift();
  if (drawn === undefined) throw new Error('deal wall too small for an opening draw');
  const dealerHand = nextHands[dealer];
  if (dealerHand === undefined) throw new Error('deal is missing the dealer hand');
  dealerHand.push(drawn);
  return {
    hands: nextHands,
    melds: emptyPerSeat<Meld>(),
    discards: emptyPerSeat<Tile>(),
    wall: remaining,
    deadWall,
    turn: dealer,
    phase: 'awaiting-discard',
    lastDiscard: null,
    drawnTile: drawn,
    result: null,
    seed,
    ruleset,
    pendingAddedKongSeat: null,
  };
}

/** Deals 4×13, then draws the dealer's opening (14th) tile. Phase: awaiting-discard. */
export function createHand(
  seed: number,
  ruleset: Ruleset = SimplifiedRuleset,
  dealer: Seat = DEALER,
): MahjongState {
  const dealt = dealHands(buildWall(seed));
  return createHandFromDeal(dealt.hands, dealt.wall, seed, ruleset, dealer);
}

/** True iff the current seat's just-completed hand is a self-drawn win. */
export function canTsumo(state: MahjongState): boolean {
  if (state.phase !== 'awaiting-discard') return false;
  const hand = state.hands[state.turn] ?? [];
  const melds = state.melds[state.turn] ?? [];
  return isWinningHand(hand, melds.length, state.ruleset);
}

/**
 * Per-seat claim availability against the open discard (empty outside
 * claim-window). During a robbing-the-kong window (`pendingAddedKongSeat`
 * set), only Ron is offered — Pung/Chi/Kong against the pending-kong tile
 * would be a made-up mechanic, not a real claim on a real discard.
 */
export function claimsFor(state: MahjongState): SeatClaims[] {
  if (state.phase !== 'claim-window' || state.lastDiscard === null) return [];
  if (state.pendingAddedKongSeat !== null) {
    const tile = state.lastDiscard.tile;
    const result: SeatClaims[] = [];
    for (let s = 0; s < SEAT_COUNT; s++) {
      if (s === state.pendingAddedKongSeat) continue;
      const seat = s as Seat;
      const hand = state.hands[seat] ?? [];
      const melds = state.melds[seat] ?? [];
      if (!canRon(hand, melds, tile, state.ruleset)) continue;
      result.push({ seat, canRon: true, canPung: false, canKong: false, chi: [] });
    }
    return result;
  }
  return availableClaims(
    state.hands,
    state.melds,
    state.lastDiscard.tile,
    state.lastDiscard.from,
    state.ruleset,
  );
}

/** Current seat discards `tileId`, opening the claim window for the others. */
export function discardTile(state: MahjongState, seat: Seat, tileId: number): MahjongState {
  requirePhase(state, 'awaiting-discard');
  if (seat !== state.turn) throw new Error(`seat ${seat} cannot discard out of turn`);
  const hand = state.hands[seat] ?? [];
  const tile = hand.find((t) => t.id === tileId);
  if (tile === undefined) throw new Error(`tile ${tileId} not in seat ${seat}'s hand`);
  return {
    ...state,
    hands: state.hands.map((h, i) => (i === seat ? h.filter((t) => t.id !== tileId) : h)),
    discards: state.discards.map((d, i) => (i === seat ? [...d, tile] : d)),
    lastDiscard: { tile, from: seat },
    drawnTile: null,
    phase: 'claim-window',
  };
}

/** Current seat declares a self-drawn win. */
export function declareTsumo(state: MahjongState, seat: Seat): MahjongState {
  requirePhase(state, 'awaiting-discard');
  if (seat !== state.turn) throw new Error(`seat ${seat} cannot declare tsumo out of turn`);
  if (!canTsumo(state)) throw new Error('no winning hand to declare tsumo');
  if (state.drawnTile === null) throw new Error('tsumo requires a drawn tile');
  return {
    ...state,
    phase: 'hand-end',
    result: { kind: 'tsumo', winner: seat, winningTile: state.drawnTile },
  };
}
